import React, { useEffect, useRef, useState } from 'react'
import {
  Link2,
  Upload,
  FileText,
  Sparkles,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Wand2,
  Languages,
  Volume2,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import Button from '../components/ui/Button'
import { useProjects } from '../hooks/useRag'
import {
  useCapabilities,
  useSubmitImport,
  useUploadImport,
  useImportJob,
  useIndexJob,
  useSaga,
} from '../hooks/useImport'
import { useDefaults } from '../hooks/useSettings'

const PROVIDERS = ['whisper', 'google', 'gemini', 'deepgram', 'assemblyai']
const TERMINAL = new Set(['completed', 'failed', 'cancelled', 'already_transcribed'])
const MEDIA_ACCEPT = '.wav,.mp3,.m4a,.flac,.ogg,.aac,.opus,.mp4,.webm,.mkv,.mov,.m4v'
const STEPS = ['Source', 'Pre-processing', 'Transcription', 'Post-processing']
const LOCAL_CAP = {
  source: 'local',
  subtitles_captions: false,
  fetch_video: false,
  requires_local_files: true,
  media_kind: 'either',
}

// Normalize an origin_type ('youtube_short', 'youtube_live', …) to the
// capability-table source key.
const canonicalOrigin = (o) => (!o ? 'youtube' : String(o).startsWith('youtube') ? 'youtube' : o)

// Media & URLs panel for the Sources tab — a focused wizard (Source →
// Pre-processing → Transcription → Post-processing) so the user isn't scrolling a
// long form. The target project comes from SourcesPage's shared ProjectBar.
// `prefill` (from Transcripts "Full pipeline…"): {channel_id, video_id, title,
// origin} — runs the wizard against an already-registered channel video.
export default function ImportPage({ embedded = false, project = '', prefill = null }) {
  const [step, setStep] = useState(0)
  const [mode, setMode] = useState('url') // 'url' | 'file'
  const [input, setInput] = useState('')
  const [debounced, setDebounced] = useState('')
  const [file, setFile] = useState(null)
  const [channelVideo, setChannelVideo] = useState(null)
  const fileInput = useRef(null)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(input.trim()), 400)
    return () => clearTimeout(t)
  }, [input])

  // Adopt a new prefill whenever Transcripts sends one (the page stays mounted).
  useEffect(() => {
    if (prefill) {
      setChannelVideo(prefill)
      setStep(0)
      setJobId(null)
      setIndexedFor(null)
    }
  }, [prefill]) // eslint-disable-line react-hooks/exhaustive-deps

  const capQuery = useCapabilities(
    channelVideo ? '' : mode === 'url' ? debounced : '',
    channelVideo ? canonicalOrigin(channelVideo.origin) : undefined
  )
  const cap = !channelVideo && mode === 'file' ? LOCAL_CAP : capQuery.data?.capability
  const source = channelVideo
    ? canonicalOrigin(channelVideo.origin)
    : mode === 'file'
      ? 'local'
      : capQuery.data?.detected

  // Step selections
  const [provider, setProvider] = useState('whisper')
  const [language, setLanguage] = useState('')
  const [captions, setCaptions] = useState('use')
  const [audioClean, setAudioClean] = useState(false)
  const [cleanup, setCleanup] = useState(false)
  const [keepVideo, setKeepVideo] = useState(false)
  const [translateOn, setTranslateOn] = useState(false)
  const [translateLang, setTranslateLang] = useState('en')
  const [ttsOn, setTtsOn] = useState(false)
  const [ttsLang, setTtsLang] = useState('en-US')

  // Seed the import steps from the saved per-call defaults (once).
  const defaultsQuery = useDefaults()
  const seededDefaults = useRef(false)
  useEffect(() => {
    const d = defaultsQuery.data?.transcribe
    const t = defaultsQuery.data?.translate
    if ((d || t) && !seededDefaults.current) {
      seededDefaults.current = true
      if (d?.provider) setProvider(d.provider)
      if (d?.language) setLanguage(d.language)
      if (d?.audio_clean) setAudioClean(true)
      if (d?.cleanup) setCleanup(true)
      if (t?.target_language) setTranslateLang(t.target_language)
    }
  }, [defaultsQuery.data])

  const projectsQuery = useProjects()
  // Language-aware import: the spoken language is an INPUT to transcription; when
  // it differs from the project language, target the translate step at the project
  // language and turn it on by default so the transcript lands in that language.
  const projLang = (projectsQuery.data?.projects ?? []).find((p) => p.name === project)?.language || 'auto'
  const projHasLang = projLang && projLang !== 'auto'
  const spoken = language.trim().toLowerCase()
  const langMismatch = projHasLang && spoken && spoken !== projLang
  useEffect(() => {
    if (projHasLang) {
      setTranslateLang(projLang)
      if (langMismatch) setTranslateOn(true)
    }
  }, [project, projLang, langMismatch]) // eslint-disable-line react-hooks/exhaustive-deps

  const submit = useSubmitImport()
  const upload = useUploadImport()
  const [jobId, setJobId] = useState(null)
  const jobQuery = useImportJob(jobId)
  const job = jobQuery.data
  const indexJob = useIndexJob()
  const [indexedFor, setIndexedFor] = useState(null)
  // Stage 2: when the BFF returned a saga_id, the server owns poll→index
  // (durable across closed tabs / BFF restarts) — the client only watches.
  const [sagaId, setSagaId] = useState(null)
  const sagaQuery = useSaga(sagaId)

  useEffect(() => {
    if (!jobId || !project || sagaId) return
    if (job?.status === 'completed' && indexedFor !== jobId) {
      setIndexedFor(jobId)
      // No artifact → BFF prefers subtitles (timestamped) when the job has them.
      indexJob.mutate({ id: jobId, project })
    }
  }, [job?.status, jobId, project, sagaId]) // eslint-disable-line react-hooks/exhaustive-deps

  const canCaptions = !!cap?.subtitles_captions
  const canVideo = !!cap?.fetch_video
  const sourceReady = channelVideo ? true : mode === 'url' ? !!debounced : !!file

  const stepOptions = () => ({
    provider,
    language: language.trim() || undefined,
    audio_clean: audioClean,
    cleanup,
    translate: translateOn ? { target_language: translateLang.trim() } : null,
    tts: ttsOn ? { language: ttsLang.trim() } : null,
  })

  const onSubmit = (e) => {
    e?.preventDefault?.()
    if (!project) return
    setIndexedFor(null)
    if (channelVideo) {
      // Already-registered channel video → transcribe by (channel, id); no URL
      // re-registration (that path derives a parallel channel from uploader id).
      const body = {
        input: channelVideo.video_id || channelVideo.title,
        channel_id: channelVideo.channel_id,
        source,
        ...stepOptions(),
        captions: canCaptions ? captions : undefined,
        keep_video: canVideo ? keepVideo : undefined,
        project,
      }
      submit.mutate(body, { onSuccess: (res) => { setJobId(res.job_id); setSagaId(res.saga_id ?? null) } })
      return
    }
    if (mode === 'file') {
      if (!file) return
      // project rides along so the server-side saga knows where to index
      upload.mutate({ file, options: { ...stepOptions(), project } }, { onSuccess: (res) => { setJobId(res.job_id); setSagaId(res.saga_id ?? null) } })
      return
    }
    if (!debounced) return
    const body = {
      input: debounced,
      source,
      ...stepOptions(),
      captions: canCaptions ? captions : undefined,
      keep_video: canVideo ? keepVideo : undefined,
      project,
    }
    submit.mutate(body, { onSuccess: (res) => { setJobId(res.job_id); setSagaId(res.saga_id ?? null) } })
  }

  const pending = submit.isPending || upload.isPending
  const running = jobId && !TERMINAL.has(job?.status)
  const canSubmit = sourceReady && !!project && !running

  return (
    <div className="space-y-6">
      {!embedded && (
        <header>
          <div className="text-[11px] uppercase tracking-[0.22em] text-gold-deep dark:text-gold mb-2">The Archive</div>
          <h1 className="font-display text-4xl text-ink dark:text-mist">Import</h1>
        </header>
      )}

      {!project && <Note kind="muted" text="Select a project above to continue." />}

      {project && (
        <div className="space-y-5">
          {/* Wizard step nav */}
          <ol className="flex flex-wrap items-center gap-1">
            {STEPS.map((label, i) => {
              const active = step === i
              const reachable = i === 0 || sourceReady
              return (
                <li key={label} className="flex items-center">
                  <button
                    type="button"
                    onClick={() => reachable && setStep(i)}
                    disabled={!reachable}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition ${
                      active ? 'bg-gold/15 text-gold-deep dark:text-gold' : 'text-ink-soft hover:text-ink dark:text-mist/60'
                    } ${!reachable ? 'opacity-40 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs ${
                        active ? 'bg-gold text-midnight-900' : 'bg-parchment-200 text-ink-soft dark:bg-midnight-700 dark:text-mist/60'
                      }`}
                    >
                      {i + 1}
                    </span>
                    {label}
                  </button>
                  {i < STEPS.length - 1 && <span className="mx-0.5 text-ink-soft/40">→</span>}
                </li>
              )
            })}
          </ol>

          {/* Current step */}
          <div className="rounded-xl border border-parchment-border dark:border-midnight-600 p-5 bg-white/50 dark:bg-midnight-800/40 min-h-[180px]">
            {step === 0 && channelVideo && (
              <Section title="Source">
                <div className="flex items-center gap-3 rounded-lg border border-gold/40 bg-gold/5 p-3.5 dark:border-gold/30">
                  <FileText className="w-4 h-4 shrink-0 text-gold-deep dark:text-gold" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-ink dark:text-mist truncate" title={channelVideo.title}>
                      {channelVideo.title}
                    </div>
                    <div className="text-xs text-ink-soft dark:text-mist/50">
                      channel video · {channelVideo.channel_id}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setChannelVideo(null)}
                    className="px-2 py-1 rounded text-xs text-ink-soft hover:text-ink dark:text-mist/50"
                    title="Detach — pick a URL or file instead"
                  >
                    ✕ detach
                  </button>
                </div>
              </Section>
            )}
            {step === 0 && !channelVideo && (
              <Section title="Source">
                <div className="inline-flex rounded-lg border border-parchment-border dark:border-midnight-600 p-0.5 mb-3">
                  {[['url', 'URL', Link2], ['file', 'Upload file', Upload]].map(([m, label, Icon]) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setMode(m)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition ${
                        mode === m ? 'bg-gold/15 text-gold-deep dark:text-gold' : 'text-ink-soft dark:text-mist/60'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                    </button>
                  ))}
                </div>

                {mode === 'url' ? (
                  <div className="relative">
                    <Link2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-soft dark:text-mist/50" />
                    <input
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder="Paste a YouTube / RuTube / TikTok / audio URL…"
                      className="w-full h-12 rounded-lg pl-11 pr-4 text-[15px] bg-white border border-gold/60 text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-2 focus:ring-spark/40 dark:bg-midnight-800 dark:border-midnight-600 dark:text-mist dark:placeholder:text-mist/40"
                    />
                  </div>
                ) : (
                  <div
                    onClick={() => fileInput.current?.click()}
                    className="rounded-lg border-2 border-dashed border-parchment-border hover:border-gold dark:border-midnight-600 dark:hover:border-gold p-6 text-center cursor-pointer"
                  >
                    <input ref={fileInput} type="file" accept={MEDIA_ACCEPT} className="hidden" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                    {file ? (
                      <span className="inline-flex items-center gap-2 text-ink dark:text-mist">
                        <FileText className="w-4 h-4 text-gold-deep dark:text-gold" /> {file.name}
                      </span>
                    ) : (
                      <span className="text-ink-soft dark:text-mist/60">Choose an audio/video file to transcribe</span>
                    )}
                  </div>
                )}

                {source && (
                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    <Pill tone="gold">source: {source}</Pill>
                    {cap?.requires_local_files && <Pill>local file</Pill>}
                    {cap?.subtitles_captions && <Pill tone="spark">captions available</Pill>}
                    {cap?.media_kind && <Pill>media: {cap.media_kind}</Pill>}
                  </div>
                )}
              </Section>
            )}

            {step === 1 && (
              <Section title="Pre-processing" hint="Fetch & prepare the audio">
                {canCaptions ? (
                  <Choice
                    label="Captions"
                    value={captions}
                    onChange={setCaptions}
                    options={[
                      ['use', 'Use captions if available'],
                      ['only', 'Captions only (no transcription)'],
                      ['skip', 'Transcribe audio (ignore captions)'],
                    ]}
                  />
                ) : (
                  <p className="text-xs text-ink-soft dark:text-mist/50">
                    {mode === 'file' ? 'Uploaded media is transcribed directly.' : 'This source has no captions — audio will be transcribed.'}
                  </p>
                )}
                <Toggle label="Clean audio (de-hum, denoise, normalize)" checked={audioClean} onChange={setAudioClean} />
                {canVideo && <Toggle label="Keep full video (not audio-only)" checked={keepVideo} onChange={setKeepVideo} />}
              </Section>
            )}

            {step === 2 && (
              <Section title="Transcription" hint="The core step" disabled={captions === 'only' && canCaptions}>
                <div className="flex flex-wrap gap-3">
                  <Field label="Provider">
                    <select
                      value={provider}
                      onChange={(e) => setProvider(e.target.value)}
                      className="h-10 rounded-lg px-3 text-sm bg-white border border-parchment-border text-ink focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
                    >
                      {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </Field>
                  <Field label="Spoken language">
                    <input
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                      placeholder="auto"
                      className="h-10 w-28 rounded-lg px-3 text-sm bg-white border border-parchment-border text-ink focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
                    />
                  </Field>
                </div>
                {projHasLang && (
                  <p className="text-xs text-ink-soft dark:text-mist/60 mt-1">
                    Project language: <span className="font-medium text-gold-deep dark:text-gold">{projLang}</span>.{' '}
                    {langMismatch
                      ? `Spoken language differs — the transcript will be translated to ${projLang} before it's indexed.`
                      : `Set the spoken language if it isn't ${projLang}, and the transcript will be translated to match.`}
                  </p>
                )}
                <Toggle label="LLM cleanup of the transcript text" checked={cleanup} onChange={setCleanup} icon={Wand2} />
              </Section>
            )}

            {step === 3 && (
              <Section title="Post-processing" hint="Optional">
                <Toggle label="Translate" checked={translateOn} onChange={setTranslateOn} icon={Languages} />
                {translateOn && (
                  <Field label="Target language" inset>
                    <input
                      value={translateLang}
                      onChange={(e) => setTranslateLang(e.target.value)}
                      placeholder="en"
                      className="h-10 w-28 rounded-lg px-3 text-sm bg-white border border-parchment-border text-ink focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
                    />
                  </Field>
                )}
                <Toggle label="Synthesize speech (TTS)" checked={ttsOn} onChange={setTtsOn} icon={Volume2} />
                {ttsOn && (
                  <Field label="Voice language" inset>
                    <input
                      value={ttsLang}
                      onChange={(e) => setTtsLang(e.target.value)}
                      placeholder="en-US"
                      className="h-10 w-28 rounded-lg px-3 text-sm bg-white border border-parchment-border text-ink focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
                    />
                  </Field>
                )}
              </Section>
            )}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between gap-3">
            <Button variant="ghost" size="sm" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}>
              ← Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button size="sm" onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))} disabled={step === 0 && !sourceReady}>
                Next →
              </Button>
            ) : (
              <Button size="lg" onClick={onSubmit} loading={pending} disabled={!canSubmit}>
                <Sparkles className="w-4 h-4" /> Import
              </Button>
            )}
          </div>

          {submit.isError && <Note kind="error" text={submit.error?.message} hint={submit.error?.hint} />}
          {upload.isError && <Note kind="error" text={upload.error?.message} hint={upload.error?.hint} />}
        </div>
      )}

      {jobId && (
        <Progress job={job} error={jobQuery.error} indexJob={indexJob} project={project} saga={sagaQuery.data} />
      )}
    </div>
  )
}

function Progress({ job, error, indexJob, project, saga }) {
  if (error) return <Note kind="error" text={`Status error: ${error.message}`} />
  if (!job) return <Note kind="muted" text="Submitting…" />

  const pct = job.progress_percent ?? 0
  const done = job.status === 'completed'
  const failed = job.status === 'failed' || job.status === 'cancelled'

  return (
    <section className="space-y-3 rounded-xl border border-parchment-border dark:border-midnight-600 p-5 bg-white/60 dark:bg-midnight-800/50">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg text-ink dark:text-mist">{job.video_title || 'Import job'}</h2>
        <span className="text-xs uppercase tracking-wide text-ink-soft dark:text-mist/50">{job.status}</span>
      </div>
      <div className="h-2 rounded-full bg-parchment-200 dark:bg-midnight-700 overflow-hidden">
        <div
          className={`h-full transition-all ${failed ? 'bg-red-500' : 'bg-gradient-to-r from-gold to-spark'}`}
          style={{ width: `${failed ? 100 : pct}%` }}
        />
      </div>
      <p className="text-sm text-ink-soft dark:text-mist/60 flex items-center gap-2">
        {!done && !failed && <Loader2 className="w-4 h-4 animate-spin" />}
        {job.progress_message || job.status} {!failed && `· ${pct}%`}
      </p>
      {job.error && <Note kind="error" text={job.error} />}

      {done && saga && <SagaIndexNote saga={saga} project={project} />}
      {done && !saga && (
        <div className="pt-1">
          {indexJob.isPending && <Note kind="muted" text={`Indexing into ${project}…`} />}
          {indexJob.isError && <Note kind="error" text={indexJob.error?.message} hint={indexJob.error?.hint} />}
          {indexJob.isSuccess && (
            <Note kind="ok" text={`Indexed ${indexJob.data.chunks_indexed ?? ''} chunks into ${project}.`}>
              <Link to="/research" className="text-gold-deep dark:text-gold underline">Search it →</Link>
            </Note>
          )}
        </div>
      )}
    </section>
  )
}

// Index status when the durable server-side saga owns it (Stage 2): reflects the
// saga's 'index' step instead of a client-side mutation. The tab can be closed —
// the work continues either way.
function SagaIndexNote({ saga, project }) {
  const idx = (saga.steps ?? []).find((s) => s.name === 'index')
  if (saga.status === 'completed') {
    const chunks = idx?.data?.chunks_indexed
    const skipped = idx?.data?.skipped
    return skipped ? (
      <Note kind="muted" text={`Done (index skipped: ${skipped}).`} />
    ) : (
      <Note kind="ok" text={`Indexed ${chunks ?? ''} chunks into ${project}.`}>
        <Link to="/research" className="text-gold-deep dark:text-gold underline">Search it →</Link>
      </Note>
    )
  }
  if (saga.status === 'compensated' || saga.status === 'failed' || saga.status === 'cancelled') {
    return <Note kind="error" text={`Import ${saga.status}: ${saga.error ?? ''}`} />
  }
  return <Note kind="muted" text={`Indexing into ${project}… (runs server-side — safe to close this tab)`} />
}

function Section({ title, hint, disabled, children }) {
  return (
    <div className={disabled ? 'opacity-50' : ''}>
      <div className="flex items-baseline gap-2 mb-3">
        <h3 className="font-display text-base text-ink dark:text-mist">{title}</h3>
        {hint && <span className="text-xs text-ink-soft dark:text-mist/50">{hint}</span>}
      </div>
      <div className="space-y-3 pl-1">{children}</div>
    </div>
  )
}

function Toggle({ label, checked, onChange, icon: Icon }) {
  return (
    <label className="flex items-center gap-2.5 cursor-pointer select-none">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#C9A227]" />
      {Icon && <Icon className="w-4 h-4 text-ink-soft dark:text-mist/50" />}
      <span className="text-sm text-ink dark:text-mist">{label}</span>
    </label>
  )
}

function Choice({ label, value, onChange, options }) {
  return (
    <div>
      <div className="text-xs text-ink-soft dark:text-mist/50 mb-1.5">{label}</div>
      <div className="flex flex-col gap-1.5">
        {options.map(([val, lbl]) => (
          <label key={val} className="flex items-center gap-2.5 cursor-pointer text-sm text-ink dark:text-mist">
            <input type="radio" name={label} checked={value === val} onChange={() => onChange(val)} className="accent-[#C9A227]" />
            {lbl}
          </label>
        ))}
      </div>
    </div>
  )
}

function Field({ label, inset, children }) {
  return (
    <div className={inset ? 'pl-7' : ''}>
      <div className="text-xs text-ink-soft dark:text-mist/50 mb-1.5">{label}</div>
      {children}
    </div>
  )
}

function Pill({ children, tone }) {
  const tones = {
    gold: 'bg-gold/15 text-gold-deep dark:text-gold border-gold/40',
    spark: 'bg-spark/10 text-spark-600 dark:text-spark border-spark/40',
    default: 'bg-parchment-200 text-ink-soft border-parchment-border dark:bg-midnight-700 dark:text-mist/60 dark:border-midnight-600',
  }
  return <span className={`px-2.5 py-1 rounded-full border ${tones[tone] || tones.default}`}>{children}</span>
}

function Note({ kind, text, hint, children }) {
  const styles = {
    ok: 'border-emerald-300/70 bg-emerald-50 text-emerald-800 dark:border-emerald-800/60 dark:bg-emerald-900/20 dark:text-emerald-300',
    error: 'border-red-300/70 bg-red-50 text-red-700 dark:border-red-800/60 dark:bg-red-900/20 dark:text-red-300',
    muted: 'border-parchment-border bg-white/60 text-ink-soft dark:border-midnight-600 dark:bg-midnight-800/50 dark:text-mist/60',
  }
  const Icon = kind === 'ok' ? CheckCircle2 : kind === 'error' ? AlertTriangle : null
  return (
    <div className={`rounded-xl border p-4 text-sm ${styles[kind]}`}>
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4" />}
        <span>{text}</span>
        {children && <span className="ml-2">{children}</span>}
      </div>
      {hint && <p className="mt-1 text-xs opacity-80">{hint}</p>}
    </div>
  )
}
